import React, { useState, useEffect } from 'react';
import Logo from '../components/Logo';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { LogOut, Search, ChevronDown, Loader2, RefreshCw, Menu } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { productsAPI, ordersAPI, adminAPI, authAPI } from '../services/api';
import ProductForm from '../components/ProductForm';
import AdminPlatesPanel from '../components/AdminPlatesPanel';
import AdminAssistPanel from '../components/AdminAssistPanel';
import AdminCustomersPanel from '../components/AdminCustomersPanel';
import AdminSidebarNav from '../components/AdminSidebarNav';
import AdminReviewsPanel from '../components/AdminReviewsPanel';
import AdminCategoriesPanel from '../components/AdminCategoriesPanel';
import AdminOrdersPanel from '../components/AdminOrdersPanel';
import { CLOSED_STATUSES } from '../components/OrderDetailDrawer';
import { toast } from 'sonner';
import AdminSettingsPanel from '../components/AdminSettingsPanel';
import AdminShippingPanel from '../components/AdminShippingPanel';
import AdminBannersPanel from '../components/AdminBannersPanel';
import AdminPromosPanel from '../components/AdminPromosPanel';
import AdminBrandsPanel from '../components/AdminBrandsPanel';
import AdminPagesPanel from '../components/AdminPagesPanel';
import AdminArticlesPanel from '../components/AdminArticlesPanel';
import AdminEmailsPanel from '../components/AdminEmailsPanel';
import AdminCronPanel from '../components/AdminCronPanel';
import AdminCartRemindersPanel from '../components/AdminCartRemindersPanel';
import AdminContactPanel from '../components/AdminContactPanel';
import AdminSellersPanel from '../components/admin/AdminSellersPanel';
import AdminListingsPanel from '../components/admin/AdminListingsPanel';
import AdminFeedsPanel from '../components/admin/AdminFeedsPanel';
import AdminMpOrdersPanel from '../components/admin/AdminMpOrdersPanel';
import AdminMpShipmentsPanel from '../components/admin/AdminMpShipmentsPanel';
import AdminMpCommercePanel from '../components/admin/AdminMpCommercePanel';
import AdminSearchMissesPanel from '../components/AdminSearchMissesPanel';
import AdminQuestionsPanel from '../components/AdminQuestionsPanel';
import AdminNotificationsBell from '../components/AdminNotificationsBell';
import AdminSuppliersPanel from '../components/AdminSuppliersPanel';
import BackInStockRequestsCard from '../components/BackInStockRequestsCard';
import AlertsConversionChart from '../components/AlertsConversionChart';
import PurchaseOrderModal from '../components/PurchaseOrderModal';
import AdminExportPanel from '../components/AdminExportPanel';
import AdminReportsPanel from '../components/AdminReportsPanel';
import AdminCrossSellPanel from '../components/AdminCrossSellPanel';
import AdminCompetitorPricesPanel from '../components/AdminCompetitorPricesPanel';
import AdminTyreFitmentsPanel from '../components/AdminTyreFitmentsPanel';
import AdminDashboardTab from '../components/admin/AdminDashboardTab';
import AdminProductsTab from '../components/admin/AdminProductsTab';
import AdminVehiclesTab from '../components/admin/AdminVehiclesTab';

const describeError = (error) => {
  const status = error.response?.status;
  if (status === 403) return "Accès refusé : ce compte n'a pas les droits administrateur.";
  if (status === 401) return 'Votre session a expiré, veuillez vous reconnecter.';
  if (!error.response) return 'Impossible de joindre le serveur. Vérifiez votre connexion puis réessayez.';
  return error.response?.data?.detail || 'Erreur lors du chargement des données.';
};

const AdminPage = () => {
  const [activeTab, setActiveTab] = useState(() => new URLSearchParams(window.location.search).get('tab') || 'dashboard');
  const [seoGradeFilter, setSeoGradeFilter] = useState(() => (new URLSearchParams(window.location.search).get('seo') || '').toUpperCase());
  const [newProductName, setNewProductName] = useState(() => new URLSearchParams(window.location.search).get('new') || '');
  const [seoSort, setSeoSort] = useState('');
  const [categoriesGradeFilter, setCategoriesGradeFilter] = useState('');
  const openSeoTab = (tab, grade) => {
    if (tab === 'products') { setSeoGradeFilter(grade); setSeoSort('seo'); }
    if (tab === 'categories') setCategoriesGradeFilter(grade === 'D' ? 'd' : 'cd');
    setActiveTab(tab);
  };
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const isAdmin = authAPI.isAdmin();
  const [productModal, setProductModal] = useState(() => (new URLSearchParams(window.location.search).get('new') ? 'new' : null)); // null | 'new' | product
  const [newProductCategory, setNewProductCategory] = useState(null);
  const [restockSupplier, setRestockSupplier] = useState(null);
  const vatRate = stats?.vat_rate ?? 20;
  const defaultMargin = stats?.target_margin_pct ?? 35;
  const location = useLocation();
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const name = sp.get('new');
    if (!name) return;
    setActiveTab(sp.get('tab') || 'products'); setNewProductName(name); setProductModal('new');
    window.history.replaceState({}, '', `/admin?tab=${sp.get('tab') || 'products'}`);
  }, [location.search]);

  const refreshProductsAndStats = () => Promise.all([productsAPI.getAll(), adminAPI.getStats()]).then(([d, st]) => { setProducts(d); setStats(st); }).catch(() => {});
  const editProductById = (id) => { const p = products.find(x => x.id === id); if (p) setProductModal(p); else setActiveTab('products'); };
  const user = authAPI.getCurrentUser();

  useEffect(() => {
    if (isAdmin) loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const loadData = async () => {
    setIsLoading(true);
    setLoadError('');
    try {
      const [productsData, ordersData, statsData] = await Promise.all([productsAPI.getAll(), ordersAPI.getAll(), adminAPI.getStats()]);
      setProducts(productsData);
      setOrders(ordersData);
      setStats(statsData);
    } catch (error) {
      console.error('Error loading admin data:', error);
      setLoadError(describeError(error));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteProduct = async (id) => {
    if (window.confirm('Voulez-vous vraiment supprimer ce produit ?')) {
      try {
        await productsAPI.delete(id);
        setProducts(products.filter(p => p.id !== id));
      } catch {
        alert('Erreur lors de la suppression');
      }
    }
  };

  const handleUpdateOrderStatus = async (orderId, status) => {
    try {
      const res = await ordersAPI.updateStatus(orderId, status);
      const restocked = CLOSED_STATUSES.includes(status);
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status, restocked } : o));
      const qty = res?.restocked_qty || 0;
      if (qty > 0) {
        toast.success(`Commande ${status.toLowerCase()} — stock remis à jour (+${qty} article${qty > 1 ? 's' : ''})`);
        adminAPI.getStats().then(setStats).catch(() => {});
      } else if (qty < 0) {
        toast.success(`Commande réouverte — stock décrémenté (${qty} article${qty < -1 ? 's' : ''})`);
        adminAPI.getStats().then(setStats).catch(() => {});
      } else {
        toast.success(`Statut mis à jour : ${status}`);
      }
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
      return false;
    }
  };

  if (!authAPI.isAuthenticated()) {
    return <Navigate to="/connexion?redirect=/admin" replace />;
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-6">
        <div className="bg-white rounded-xl shadow-sm p-8 max-w-md text-center" data-testid="admin-forbidden">
          <h1 className="text-xl font-bold text-gray-900 mb-2">Accès réservé aux administrateurs</h1>
          <p className="text-gray-500 mb-6">Le compte <strong>{user?.email}</strong> n'a pas les droits d'administration.</p>
          <div className="flex justify-center gap-3">
            <Link to="/" className="px-4 py-2 rounded-md bg-orange-500 text-white hover:bg-orange-600">Retour à l'accueil</Link>
            <Link to="/connexion?redirect=/admin" onClick={() => authAPI.logout()} className="px-4 py-2 rounded-md border hover:bg-gray-50">Changer de compte</Link>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex" translate="no" lang="fr" data-testid="admin-root">
      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />}
      <aside data-testid="admin-sidebar" className={`w-64 bg-[#0e0e10] text-white flex flex-col fixed inset-y-0 left-0 z-40 transform transition-transform duration-200 lg:static lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-6 border-b border-[#26262a]">
          <Link to="/" className="inline-block">
            <Logo variant="dark" size="md" />
          </Link>
          <p className="text-xs text-gray-400 mt-1">Administration</p>
        </div>

        <nav className="flex-1 min-h-0 overflow-y-auto p-4" data-testid="admin-sidebar-nav">
          <AdminSidebarNav activeTab={activeTab} stats={stats} onSelect={(id) => { setActiveTab(id); setSidebarOpen(false); }} />
        </nav>

        <div className="p-4 border-t border-[#26262a]">
          <Link to="/" onClick={() => authAPI.logout()} className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:text-white transition-colors">
            <LogOut size={20} />
            Déconnexion
          </Link>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-white shadow-sm px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button data-testid="admin-menu-toggle" onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 -ml-2 text-gray-700 hover:bg-gray-100 rounded-md" aria-label="Ouvrir le menu">
              <Menu size={22} />
            </button>
            <div className="relative flex-1 min-w-0 max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <Input placeholder="Nom, marque, référence, OEM..." data-testid="admin-product-search-input" className="pl-10 w-full sm:w-64" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-4 shrink-0">
            <AdminNotificationsBell stats={stats} onNavigate={(tab) => { setActiveTab(tab); setSidebarOpen(false); }} />
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white font-medium">{user?.first_name?.charAt(0) || 'A'}</div>
              <span className="text-sm font-medium text-gray-700 hidden sm:inline">{user?.first_name || 'Admin'}</span>
              <ChevronDown size={16} className="text-gray-400" />
            </div>
          </div>
        </header>

        <main className="flex-1 p-4 sm:p-6 overflow-auto">
          {loadError && (
            <div data-testid="admin-load-error" className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex flex-wrap items-center justify-between gap-3 text-sm text-red-700">
              <span>{loadError}</span>
              <Button size="sm" variant="outline" onClick={loadData} data-testid="admin-retry">
                <RefreshCw size={14} className="mr-1" /> Réessayer
              </Button>
            </div>
          )}

          {activeTab === 'dashboard' && (
            <AdminDashboardTab
              stats={stats} setStats={setStats} orders={orders} setActiveTab={setActiveTab} openSeoTab={openSeoTab}
              onEditProduct={editProductById} onNewProduct={(category, subcategory) => { setNewProductCategory({ category, subcategory }); setProductModal('new'); }}
              onPrepareRestock={setRestockSupplier} onStatusChange={handleUpdateOrderStatus}
            />
          )}

          {activeTab === 'products' && (
            <AdminProductsTab
              products={products} setProducts={setProducts} searchTerm={searchTerm} vatRate={vatRate} defaultMargin={defaultMargin}
              seoGradeFilter={seoGradeFilter} setSeoGradeFilter={setSeoGradeFilter} seoSort={seoSort} setSeoSort={setSeoSort}
              onEdit={setProductModal} onNew={() => setProductModal('new')} onDelete={handleDeleteProduct} onEditById={editProductById}
              loadData={loadData} refreshProductsAndStats={refreshProductsAndStats} setActiveTab={setActiveTab}
            />
          )}

          {activeTab === 'cross-sell' && <AdminCrossSellPanel />}
          {activeTab === 'competitors' && <AdminCompetitorPricesPanel onPriceChanged={refreshProductsAndStats} onEditProduct={editProductById} />}
          {activeTab === 'orders' && <AdminOrdersPanel orders={orders} onReload={loadData} onStatusChange={handleUpdateOrderStatus} onOrderUpdated={(u) => setOrders(prev => prev.map(o => o.id === u.id ? { ...o, ...u } : o))} />}
          {activeTab === 'vehicles' && <AdminVehiclesTab onError={(e) => setLoadError(describeError(e))} />}
          {activeTab === 'plates' && <AdminPlatesPanel />}
          {activeTab === 'assist' && <AdminAssistPanel />}
          {activeTab === 'tyre-fitments' && <AdminTyreFitmentsPanel />}
          {activeTab === 'customers' && <AdminCustomersPanel />}
          {activeTab === 'reviews' && <AdminReviewsPanel />}
          {activeTab === 'categories' && <AdminCategoriesPanel gradeFilter={categoriesGradeFilter} onGradeFilter={setCategoriesGradeFilter} />}
          {activeTab === 'brands' && <AdminBrandsPanel />}
          {activeTab === 'banners' && <AdminBannersPanel />}
          {activeTab === 'promos' && <AdminPromosPanel />}
          {activeTab === 'cart-reminders' && <AdminCartRemindersPanel />}
          {activeTab === 'pages' && <AdminPagesPanel />}
          {activeTab === 'articles' && <AdminArticlesPanel />}
          {activeTab === 'emails' && <AdminEmailsPanel />}
          {activeTab === 'crons' && <AdminCronPanel onNavigate={setActiveTab} />}
          {activeTab === 'contact' && <AdminContactPanel />}
          {activeTab === 'sellers' && <AdminSellersPanel />}
          {activeTab === 'listings' && <AdminListingsPanel />}
          {activeTab === 'feeds' && <AdminFeedsPanel />}
          {activeTab === 'mporders' && <AdminMpOrdersPanel />}
          {activeTab === 'mpshipments' && <AdminMpShipmentsPanel />}
          {activeTab === 'mpcommerce' && <AdminMpCommercePanel />}
          {activeTab === 'search-misses' && <AdminSearchMissesPanel />}
          {activeTab === 'questions' && <AdminQuestionsPanel onPendingChange={(n) => setStats(s => (s ? { ...s, pending_questions: n } : s))} />}
          {activeTab === 'export' && <AdminExportPanel />}
          {activeTab === 'reports' && <AdminReportsPanel onOpenSettings={() => setActiveTab('settings')} />}
          {activeTab === 'suppliers' && <><BackInStockRequestsCard onEditProduct={editProductById} /><AlertsConversionChart /><AdminSuppliersPanel onEditProduct={editProductById} onStockChanged={refreshProductsAndStats} /></>}
          {activeTab === 'settings' && <AdminSettingsPanel />}
          {activeTab === 'shipping' && <AdminShippingPanel />}
        </main>
      </div>

      {restockSupplier && (
        <PurchaseOrderModal supplier={restockSupplier} onClose={() => setRestockSupplier(null)} onCreated={() => { setRestockSupplier(null); refreshProductsAndStats(); }} />
      )}

      {productModal && (
        <ProductForm
          vatRate={vatRate}
          defaultMargin={defaultMargin}
          product={productModal === 'new' ? null : productModal}
          initialCategory={productModal === 'new' ? newProductCategory : null}
          initialName={productModal === 'new' ? newProductName : ''}
          onClose={() => { setProductModal(null); setNewProductCategory(null); }}
          onSubmit={async (payload) => {
            if (productModal === 'new') await productsAPI.create(payload);
            else await productsAPI.update(productModal.id, payload);
            const [data, statsData] = await Promise.all([productsAPI.getAll(), adminAPI.getStats()]);
            setProducts(data);
            setStats(statsData);
          }}
        />
      )}
    </div>
  );
};

export default AdminPage;
